/**
 * An employer name that opens on LinkedIn.
 *
 * Hovering (or keyboard-focusing) shows exactly what will open: the company
 * name and the query behind the link, so nobody has to guess where a click
 * goes. Clicks are logged so we can see which employers learners actually
 * follow up on.
 *
 * Display-only — nothing here affects scoring.
 */
import { ExternalLink } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { employerLink, type EmployerLinkKind } from "@/lib/employerLinks";
import { track } from "@/lib/analytics";

export interface EmployerLinkProps {
  company: string;
  /** "jobs" opens openings, "company" opens the LinkedIn profile. */
  kind?: EmployerLinkKind;
  /** Career title, used to sharpen the fallback jobs search. */
  role?: string;
  /** Where this link lives, for analytics. */
  surface?: string;
  /** Show the small external-link glyph. */
  showIcon?: boolean;
  className?: string;
}

export default function EmployerLink({
  company,
  kind = "jobs",
  role,
  surface = "career_intel",
  showIcon = true,
  className = "",
}: EmployerLinkProps) {
  const link = employerLink(company, kind, role);

  const onOpen = () => {
    track("linkedin_employer_click", {
      surface,
      props: {
        company,
        kind,
        role: role ?? null,
        href: link.href,
        exact: link.exact,
      },
    });
  };

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onOpen}
            onAuxClick={onOpen}
            aria-label={`${company} — ${link.query} (opens in a new tab)`}
            className={
              className ||
              "inline-flex min-h-[32px] items-center gap-1 rounded-lg border border-border/60 bg-background/60 px-2 py-1 text-xs text-foreground/90 underline-offset-4 transition-colors hover:border-primary/60 hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            }
          >
            {company}
            {showIcon && <ExternalLink className="h-3 w-3 opacity-60" aria-hidden="true" />}
          </a>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px]">
          <p className="text-xs font-semibold">{company}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{link.query}</p>
          <p className="mt-1 text-[11px] text-primary">
            {link.exact
              ? "Real employers hiring for this path — opens on LinkedIn."
              : "Live LinkedIn results for this employer — see who is hiring today."}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
